import React, {
  useState,
  useContext,
  useImperativeHandle,
  forwardRef,
  useRef,
  useEffect,
} from "react";
import { Divider, Switch, Grid, Icon } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import toast from "react-hot-toast";
import { UserContext } from "App";
import SessionsApi from "Classes/API/Administration/SessionsApi";
import BodyDiagramCanvas from "./BodyDiagramCanvas";
import { convertSvgToFile } from "./formHelpers";
import { uploadPatientDiagram } from "./uploadService";
import bodyImageUrl from "../../assets/images/Patient.png";
import NotesUrl from "../../assets/images/FreeDraw.png";

const FirstStage = forwardRef(({ patientId }, ref) => {
  const User = useContext(UserContext);
  const containerRef = useRef(null);
  const [canvasWidth, setCanvasWidth] = useState(600);
  const [formData, setFormData] = useState({
    physicalDiagnosis: "",
    chiefComplaint: "",
    medicalHistory: "",
    pastHistory: "",
    findings: "",
    planOfTreatment: "",
  });
  const [bodyDiagramSvg, setBodyDiagramSvg] = useState(null);
  const [freeNotesSvg, setFreeNotesSvg] = useState(null);
  const [freeNotesToggle, setFreeNotesToggle] = useState(false);

  useEffect(() => {
    if (containerRef.current) {
      setCanvasWidth(containerRef.current.offsetWidth);
    }
  }, []);

  const dynamicHeight = (canvasWidth * 530) / 600;
  const dynamicHeightFree = (canvasWidth * 1000) / 700;

  useImperativeHandle(ref, () => ({
    submit: async () => {
      try {
        let bodyUrl = bodyDiagramSvg
          ? await uploadPatientDiagram(
              convertSvgToFile(bodyDiagramSvg, "body.svg"),
              patientId,
            )
          : null;
        let freeNotesUrl =
          freeNotesToggle && freeNotesSvg
            ? await uploadPatientDiagram(
                convertSvgToFile(freeNotesSvg, "notes.svg"),
                patientId,
              )
            : null;

        await SessionsApi.CreatePhysicalDiagnosis({
          PatientId: patientId,
          ...formData,
          ClinicalFindings: formData.findings,
          FinalDiagnosis: formData.planOfTreatment,
          BodyDiagramUrl: bodyUrl,
          FreeNotesDiagramUrl: freeNotesUrl,
          CreatedById: User.id,
        });
        toast.success("Saved!");
        return true;
      } catch (error) {
        toast.error(error.message);
        return false;
      }
    },
  }));

  return (
    <MDBox
      ref={containerRef}
      display="flex"
      flexDirection="column"
      gap={4}
      px={2}
      py={2}
    >
      <MDBox
        sx={{
          width: "100%",
          maxWidth: "1400px",
          mx: "auto",
          backgroundColor: "#fff",
          borderRadius: "16px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        }}
      >
        <MDTypography variant="h6" fontWeight="bold" color="info" mb={1} ml={1}>
          Body Mapping
        </MDTypography>
        <BodyDiagramCanvas
          bodyImageUrl={bodyImageUrl}
          onSave={setBodyDiagramSvg}
          imageHight={dynamicHeight}
          useZoomedExport={true}
        />
      </MDBox>

      <Divider sx={{ my: 1 }} />

      <MDBox sx={{ width: "100%", mx: "auto" }}>
        <MDTypography variant="h6" fontWeight="bold" color="info" mb={2}>
          Clinical Details
        </MDTypography>
        <Grid container spacing={3}>
          {Object.keys(formData).map((key) => (
            <Grid item xs={12} md={6} key={key}>
              <MDInput
                fullWidth
                multiline
                label={key
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase())
                  .trim()}
                rows={3}
                value={formData[key]}
                onChange={(e) =>
                  setFormData({ ...formData, [key]: e.target.value })
                }
              />
            </Grid>
          ))}
        </Grid>
      </MDBox>

      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={2}
        sx={{
          bgcolor: "#e3f2fd",
          borderRadius: 3,
          maxWidth: "1400px",
          width: "100%",
          mx: "auto",
          border: "1px solid #bbdefb",
        }}
      >
        <MDBox display="flex" alignItems="center" gap={1}>
          <Icon color="info">edit_note</Icon>
          <MDTypography variant="button" fontWeight="bold" color="info">
            Enable Free Hand Sketch Notes
          </MDTypography>
        </MDBox>
        <Switch
          checked={freeNotesToggle}
          onChange={() => setFreeNotesToggle(!freeNotesToggle)}
        />
      </MDBox>

      {freeNotesToggle && (
        <MDBox
          sx={{
            width: "100%",
            maxWidth: "1400px",
            mx: "auto",
            backgroundColor: "#fff",
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            p: 1,
          }}
        >
          <MDTypography
            variant="h6"
            fontWeight="bold"
            color="info"
            mb={1}
            ml={1}
          >
            Free Hand Sketch
          </MDTypography>
          <BodyDiagramCanvas
            bodyImageUrl={NotesUrl}
            onSave={setFreeNotesSvg}
            imageHight={dynamicHeightFree}
            useZoomedExport={true}
          />
        </MDBox>
      )}
    </MDBox>
  );
});

export default FirstStage;
